
import { defineComponent, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { mapGetters, useStore } from 'vuex';

import { useAuthentication_Get_Login_OptionsQuery, useLicenseLazyQuery } from '@/graphql/codegen/generated/graphql';
import { IKPlatformAuthentication } from '@/modules/platformAuthentication/platformAuthentication.injectionKeys';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { Routes } from '@/router/router.const';
import { useOsService } from '@/services/OsService/OsService';
import { IKEventHub } from '@/types/injectionKeys';
import { envConfig, envFeatureFlags } from '@/services/features';
import LoginForm from '@/views/login/loginComponents/loginForm.vue';
import NetworkError from '@/views/login/loginComponents/networkError.vue';

export default defineComponent({
  name: 'Login',
  components: {
    LoginForm,
    NetworkError,
  },
  emits: ['loading'],
  setup() {
    const { t } = useI18n();

    const coordinatorsService = inject(IKCoordinatorsService);
    const platformAuthentication = inject(IKPlatformAuthentication);
    const eventHub = inject(IKEventHub);
    const store = useStore();
    const coordId = ref('');
    const GQL_status = useAuthentication_Get_Login_OptionsQuery({ fetchPolicy: 'network-only' });
    const license = useLicenseLazyQuery(
      () => ({
        coordinatorId: coordId.value,
      }),
      { fetchPolicy: 'network-only' },
    );
    const osService = useOsService();

    return { eventHub, store, platformAuthentication, coordinatorsService, GQL_status, license, isWindows: osService.isWindows, coordId, t };
  },
  data() {
    return {
      loading: false,
      loginFailed: false,
      usernameCache: false as boolean | string,
      passwordCache: false as boolean | string,
      connectionLost: false,
    };
  },
  computed: {
    ...mapGetters({
      ldState: ['main/leftDrawer'] as any,
    }),
  },
  mounted() {
    if (this.isConnectionLost()) {
      this.connectionLost = true;
    }
  },
  methods: {
    isGreenOrRed(color: 'green' | 'red') {
      const isGreen = this.platformAuthentication?.getAccessToken() || !this.usernameCache;
      if (color === 'green') return isGreen;
      if (color === 'red') return !isGreen;
      return null;
    },
    isConnectionLost() {
      // TODO GraphQL intergrate with healthCheck
      return false;
    },
    setLoadingPhase(phase: boolean) {
      this.$emit('loading', phase);
      this.loading = phase;
    },
    async login(event: any) {
      const { username, password } = event;
      this.setLoadingPhase(true);
      this.loginFailed = false;
      this.passwordCache = false;
      try {
        const loginRes = await this.platformAuthentication?.login({ username, password });

        const token = loginRes?.accessToken || '';
        if (token) {
          if (this.isWindows) {
            const coordinators = await this.coordinatorsService?.fetchCoordinators(token);
            const [winCoordinator = ''] = Array.from(coordinators || []);
            this.coordId = winCoordinator;
          } else {
            // currently we have only one coordinator for linux and its id is 1
            this.coordId = '1';
          }
          this.license.load();
          this.license.onResult(async result => {
            const lastRoute = sessionStorage.getItem('lastRoute') || '';
            const isTrial = envFeatureFlags.isFreeTrial && result.data?.license?.windows?.isFreeTrial;
            const [nextRoute] = [isTrial ? Routes.GettingStarted : lastRoute, envConfig.defaultRoute].filter(Boolean);
            await apolloClient.cache.reset();
            this.coordinatorsService?.reset();
            await this.$router.push({ name: nextRoute });
            this.setLoadingPhase(true);
          });
        } else {
          this.loginFailed = true;
          this.setLoadingPhase(false);
        }
      } catch (error) {
        if ((error as any)?.message === 'Network request failed') {
          this.connectionLost = true;
          this.usernameCache = username;
          this.passwordCache = password;
        } else {
          this.connectionLost = false;
          this.loginFailed = true;
        }
        this.setLoadingPhase(false);
      }
    },
  },
});
